import { useEffect, useState } from 'react';
import { ChevronLeft, Voicemail as VoicemailIcon } from 'lucide-react';
import ExtensionStatus from '../components/ExtensionStatus.jsx';
import Pill from '../components/Pill.jsx';
import CallHistory from './CallHistory.jsx';
import { api } from '../lib/api.js';
import { Link } from '../lib/router.jsx';
import { formatMinutes } from '../lib/format.js';

/* One extension, in full: who they are, today's figures, and their calls.

   Reached from the Extensions list. Everything shown is still fetched under
   the session scope — the extension in the URL narrows an admin's view, it
   doesn't widen anyone's. An extension user who types another extension into
   the address bar gets their own calls back, because the backend pins them. */
export default function ExtensionDetail({ extension }) {
  const [state, setState] = useState({ status: 'loading', ext: null, stats: null, error: null });

  useEffect(() => {
    const ac = new AbortController();

    Promise.all([
      api.get('/extensions', { signal: ac.signal }),
      api.get(`/stats?extension=${encodeURIComponent(extension)}`, { signal: ac.signal }).catch(() => null),
    ])
      .then(([{ items }, statsRes]) => {
        const ext = items.find((e) => e.extension === extension) ?? null;
        setState({ status: 'ready', ext, stats: statsRes?.stats ?? null, error: null });
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setState({ status: 'error', ext: null, stats: null, error: err });
      });

    return () => ac.abort();
  }, [extension]);

  const { ext, stats } = state;

  return (
    <div>
      <Link
        to="/extensions"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none',
          color: 'var(--ink-soft)', fontSize: 13.5, fontWeight: 500, marginBottom: 14,
        }}
      >
        <ChevronLeft size={15} /> All extensions
      </Link>

      <div className="card" style={{ padding: '18px 20px', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div className="avatar" style={{ width: 42, height: 42, fontSize: 15 }}>
            {(ext?.name ?? extension).slice(0, 2).toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 18 }}>
              {state.status === 'loading' ? 'Loading…' : (ext?.name ?? `Extension ${extension}`)}
            </div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--signal-deep)' }}>
              ext {extension}
            </div>
          </div>
          {ext && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <ExtensionStatus status={ext.status} device={ext.device} />
              {/* Not "voicemail on/off" — the platform doesn't tell us. */}
              {ext.voicemail_to_email && (
                <Pill tone="offline">
                  <VoicemailIcon size={11} style={{ verticalAlign: -1, marginRight: 4 }} />
                  {ext.voicemail_to_email}
                </Pill>
              )}
            </div>
          )}
        </div>

        {stats && (
          <div
            style={{
              display: 'flex', gap: 28, marginTop: 18, paddingTop: 16,
              borderTop: '1px solid var(--line)', flexWrap: 'wrap',
            }}
          >
            <Figure label="Calls today" value={stats.inbound.total} />
            <Figure label="Answered" value={stats.inbound.answered} />
            {stats.inbound.answered_elsewhere > 0 && (
              <Figure label="Taken by a colleague" value={stats.inbound.answered_elsewhere} />
            )}
            <Figure label="Missed" value={stats.inbound.missed} tone="var(--miss)" />
            <Figure label="On calls" value={formatMinutes(stats.inbound.talk_seconds)} />
          </div>
        )}
      </div>

      {/* The same table as Call history, narrowed to this extension. */}
      <CallHistory extension={extension} />
    </div>
  );
}

function Figure({ label, value, tone }) {
  return (
    <div>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 20, color: tone }}>
        {value}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--ink-soft)' }}>{label}</div>
    </div>
  );
}
